import * as React from 'react';
import { Formik, FormikHelpers } from 'formik';
import { CompanyForm, validateCompany, CompanyValues } from '../components';
import { Region, useAuth } from 'lib/context';
import { updateCustomer } from 'lib/api';
import { successToast } from 'lib/components/toasts/success';
import { errorToast } from 'lib/components/toasts/error';
import { useCreateRegion } from 'lib/api/region/createRegion';
import { useGetRegionsQuery } from 'lib/api/region/getResellerRegions';
import { MarkAsSentOptions } from 'lib/const/MarkAsSentOptions';
import { CHANGE_ORIGIN } from 'lib/const/AccountChange';
import { useUploadFile } from 'lib/hooks/useUploadFile';
import { useToastError } from 'lib/hooks';
import { IImgProps } from 'lib/components/formik/FormikImageUpload/types';
interface ReceivedData {
  business: string;
  firstName: string;
  lastName?: string;
  phone1: string;
  phone2?: string;
  fax1: string;
  email: string;
  url: string;
  allowReplies: number;
  preloadTeleprompterScripts: number;
  quoteSettings: string;
  reactionsPreference: number;
  regionId: number;
  markVideosAsSent?: string;
  credit700Url?: string;
  mapIframeEmbedCode: string | null;
  autominerGroupId?: string;
  leadsNotificationsAccess: number;
  customerLogo?: string;
}

type ProfileProps = {
  editCompanyName?: boolean;
};

let fields = {
  businessName: '',
  firstNameBusiness: '',
  lastNameBusiness: '',
  businessPhone1: '',
  businessPhone2: '',
  businessFax: '',
  businessEmail: '',
  businessUrl: '',
  allowReplies: false,
  preloadTeleprompterScripts: false,
  quoteSettings: '',
  markVideosAsSent: '',
  credit700Url: '',
  region: {
    value: 0,
    label: '',
  },
  leadsNotificationsAccess: false,
};

const preparePatchData = (values: CompanyValues) => {
  values.businessName = values.businessName.trim();
  values.firstNameBusiness = values.firstNameBusiness.trim();
  values.lastNameBusiness = values.lastNameBusiness.trim();

  const data: ReceivedData = {
    business: values.businessName || '',
    firstName: values.firstNameBusiness,
    lastName: values.lastNameBusiness,
    phone1: values.businessPhone1,
    phone2: values.businessPhone2,
    fax1: values.businessFax || '',
    email: values.businessEmail,
    url: values.businessUrl || '',
    mapIframeEmbedCode: values.mapIframeEmbedCode || null,
    allowReplies: values.allowReplies === true ? 1 : 0,
    preloadTeleprompterScripts:
      values.preloadTeleprompterScripts === true ? 1 : 0,
    quoteSettings: values.quoteSettings,
    reactionsPreference: values.reactionsPreference,
    markVideosAsSent: values.markVideosAsSent,
    credit700Url: values.credit700Url,
    regionId: Number(values?.region?.value) || 0,
    autominerGroupId: values.autominerGroupId || '',
    leadsNotificationsAccess: values.leadsNotificationsAccess === true ? 1 : 0,
  };
  return data;
};

const loadFields = (data: any) => {
  return {
    businessName: data.business || '',
    firstNameBusiness: data.firstName || '',
    lastNameBusiness: data.lastName || '',
    businessPhone1: data.phone1 || '',
    businessPhone2: data.phone2 || '',
    businessFax: data.fax1 || '',
    businessEmail: data.email || '',
    businessUrl: data.url || '',
    allowReplies: data.allowReplies === '1' ? true : false,
    preloadTeleprompterScripts:
      data.preloadTeleprompterScripts === '1' ? true : false,
    quoteSettings: data.quoteSettings,
    reactionsPreference: data.reactionsPreference,
    mapIframeEmbedCode: data.mapIframeEmbedCode || '',
    credit700Url: data.credit700Url || '',
    autominerGroupId: data.autominerGroupId || '',
    markVideosAsSent:
      data.markVideosAsSent ?? MarkAsSentOptions.ASK_VERY_TIME.value.toString(),
    region: {
      value: data.regionId,
      label:
        data.regions.find(
          (reg: Region) =>
            reg.regionId?.toString() === data.regionId?.toString()
        )?.name || '',
    },
    resellerId: data.resellerId || 0,
    leadsNotificationsAccess:
      data.leadsNotificationsAccess === '1' ? true : false,
    customerLogo: { file: null, url: data?.customerLogo || '' },
  };
};

export const EditCompanyProfile = (props: ProfileProps) => {
  const { userData, invalidateUser } = useAuth();
  const { uploadFile } = useUploadFile();
  const { showError } = useToastError();

  const { editCompanyName = true } = props;
  const { mutateAsync: createRegion } = useCreateRegion();

  const { data: regions } = useGetRegionsQuery(userData.hasRegions);

  const submitData = async (
    values: CompanyValues,
    formik?: FormikHelpers<CompanyValues>
  ) => {
    try {
      const data = preparePatchData(values);
      if (
        userData.hasRegions &&
        !values.region?.value &&
        values.region?.label
      ) {
        // create new region first
        const region = await createRegion(values.region.label.toString());

        data.regionId = region.regionId;
      }

      const logoData = values.customerLogo as IImgProps;

      // new logo file was selected in the form.
      if (logoData?.file) {
        try {
          const uploadedUrl = await uploadFile(logoData.file);
          if (!uploadedUrl) {
            showError('Failed to get URL from logo upload.');
            return;
          }
          // set the property to the new URL string for the backend
          data.customerLogo = uploadedUrl;
        } catch (error) {
          showError('Failed to upload logo');
          return;
        }
      }
      // the logo was removed (URL is empty, no new file).
      else if (logoData?.url === '') {
        data.customerLogo = '';
      }
      // the logo is unchanged.
      else {
        // the value is an existing URL
        data.customerLogo = logoData.url;
      }

      await updateCustomer(
        {
          customerId: userData.customerId,
          data,
        },
        CHANGE_ORIGIN.companyProfile
      );
      await invalidateUser();
      successToast({ title: 'Your company information has been saved.' });
      formik?.resetForm();
    } catch {
      errorToast({
        title:
          'An error occurred saving your information, please try again later.',
      });
    }
  };

  return (
    <Formik
      initialValues={
        {
          ...fields,
          ...loadFields({ ...userData.customer, regions: regions || [] }),
        } as CompanyValues
      }
      validate={validateCompany}
      onSubmit={submitData}
      enableReinitialize
    >
      {props => (
        <CompanyForm
          {...props}
          regions={regions || []}
          editCompanyName={editCompanyName}
          isAutomotiveServiceRole={userData.isAutomotiveServiceRole}
          isAutomotive={userData.isAutomotive}
          resellerId={userData.customer.resellerId}
          submitData={submitData}
        />
      )}
    </Formik>
  );
};
